import { environmentalizeCAPSURL, toURL, type LocationName } from "@credit-acceptance/common-frontend";

/** Hides the browser PDF viewer's toolbar and side panel. */
const HIDE_TOOLBAR_NAVPANES = "toolbar=0&navpanes=0";

/** The CAPS Dealer Setup page. */
const DEALER_SETUP_PATH = "/steps/pages/dealersetup/dealersetup_main.jsf?origin=liferay";

/** Returns the URL's protocol, or undefined when it can't be parsed. */
export function protocolOf(source: string): string | undefined {
  try {
    return new URL(source, globalThis.location?.href).protocol;
  } catch {
    return undefined;
  }
}

/**
 * Resolves a CAPS location to its URL, falling back to an empty string when
 * it can't be resolved (e.g. local dev outside the CAPS frame).
 * @param {LocationName} name - The symbolic CAPS location name.
 * @returns {string} The resolved URL, or an empty string on failure.
 */
export function resolveDocumentUrl(name: LocationName): string {
  try {
    return toURL(name);
  } catch {
    return "";
  }
}

/**
 * Appends a deep-link anchor to a document's base URL, targeting a section
 * via the PDF's named destination when a policyTag is present.
 * @param {string} baseUrl - The document's base URL.
 * @param {string} [policyTag] - A policyTag matching a named destination in the PDF.
 * @returns {string} The document URL, opened with the toolbar/navpanes hidden.
 */
function withPolicyAnchor(baseUrl: string, policyTag?: string): string {
  const anchor = policyTag
    ? `#nameddest=${encodeURIComponent(policyTag)}&${HIDE_TOOLBAR_NAVPANES}`
    : `#${HIDE_TOOLBAR_NAVPANES}`;
  return `${baseUrl}${anchor}`;
}

/**
 * Builds the stipulation guide PDF URL, deep-linking to a policy's section
 * via the PDF's named destination when a policyTag is present.
 * @param {string} [policyTag] - The stipulation's policyTag, matching a named destination in the PDF.
 * @returns {string} The stipulation guide URL, opened with the toolbar/navpanes hidden, or an empty
 * string when the CAPS location can't be resolved (e.g. local dev outside the CAPS frame).
 */
export function stipulationGuideUrl(policyTag?: string): string {
  const baseUrl = resolveDocumentUrl("stipulationGuide");
  return baseUrl ? withPolicyAnchor(baseUrl, policyTag) : "";
}

/**
 * Resolves a CAPS pathname to an absolute URL for the current environment,
 * falling back to an empty string when it can't be resolved (e.g. local dev
 * outside the CAPS frame).
 * @param {string} pathname - The CAPS pathname, e.g. "/steps/pages/dealersetup/dealersetup_main.jsf".
 * @returns {string} The absolute CAPS URL, or an empty string on failure.
 */
export function capsUrl(pathname: string): string {
  try {
    return environmentalizeCAPSURL(pathname);
  } catch {
    return "";
  }
}

/**
 * Builds the CAPS Dealer Setup page URL for the current environment.
 * @returns {string} The Dealer Setup URL, or an empty string when it can't be resolved (e.g. local
 * dev outside the CAPS frame).
 */
export function dealerSetupUrl(): string {
  return capsUrl(DEALER_SETUP_PATH);
}
